import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import {
  confirmPasswordReset,
  getAuthMode,
  getAuthStatus,
  getProfile,
  login,
  loginMultiUser,
  logout,
  requestPasswordReset,
  setup,
  signup,
  suggestMetrics,
  updateProfile,
  type AuthModeResponse,
  type AuthStatus,
  type LoginMultiUserRequest,
  type PasswordResetConfirmBody,
  type PasswordResetRequestBody,
  type Profile,
  type ProfileUpdateRequest,
  type SetupRequest,
  type SetupResponse,
  type SignupRequest,
  type SignupResponse,
  type SuggestMetricsResponse,
} from './api';

/**
 * Hooks de auth y perfil basados en TanStack Query.
 *
 * Convenciones:
 * - `['auth','status']` se invalida en setup, login, logout.
 * - `['profile']` solo se habilita si `authenticated === true`.
 */

export const AUTH_STATUS_QUERY_KEY = ['auth', 'status'] as const;
export const PROFILE_QUERY_KEY = ['profile'] as const;

export function useAuthStatus(): UseQueryResult<AuthStatus, Error> {
  return useQuery<AuthStatus, Error>({
    queryKey: AUTH_STATUS_QUERY_KEY,
    queryFn: getAuthStatus,
    staleTime: 0,
    retry: 1,
  });
}

export function useProfile(options?: { enabled?: boolean }): UseQueryResult<Profile, Error> {
  return useQuery<Profile, Error>({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: getProfile,
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useSetup(): UseMutationResult<SetupResponse, Error, SetupRequest> {
  const qc = useQueryClient();
  return useMutation<SetupResponse, Error, SetupRequest>({
    mutationFn: setup,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth'] });
      await qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

export function useLogin(): UseMutationResult<{ ok: true }, Error, string> {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (passphrase: string) => login(passphrase),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth'] });
      await qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

export function useLogout(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => logout(),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: PROFILE_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

export function useUpdateProfile(): UseMutationResult<Profile, Error, ProfileUpdateRequest> {
  const qc = useQueryClient();
  return useMutation<Profile, Error, ProfileUpdateRequest>({
    mutationFn: updateProfile,
    onSuccess: (data) => {
      qc.setQueryData(PROFILE_QUERY_KEY, data);
    },
  });
}

/**
 * Sugerencias de FTP / LTHR / T-pace a partir de actividades recientes.
 *
 * Es un hook de **fetch manual** (no auto-load): `enabled: false` por defecto
 * — el caller dispara `refetch()` cuando el usuario pulsa el botón. Esto evita
 * golpear el endpoint al montar el form si el usuario solo quiere editar el
 * nombre.
 */
export function useSuggestMetrics(
  days = 30,
): UseQueryResult<SuggestMetricsResponse, Error> {
  return useQuery<SuggestMetricsResponse, Error>({
    queryKey: ['profile', 'suggest-metrics', days] as const,
    queryFn: () => suggestMetrics(days),
    enabled: false,
    // No cacheamos largo: las actividades cambian con el tiempo. Un minuto
    // es suficiente para que reintentos seguidos no golpeen el backend.
    staleTime: 60_000,
    retry: 0,
  });
}

// ---------------------------------------------------------------------------
// M2.42 — multi-user
// ---------------------------------------------------------------------------
//
// El modo (single-user passphrase vs multi-user email+password) lo decide el
// backend al arrancar y no cambia en vida de la app. Por eso `useAuthMode` usa
// `staleTime: Infinity` y cachea agresivo. Los hooks de mutation invalidan la
// query de status al éxito para que el router gating reaccione.

export const AUTH_MODE_QUERY_KEY = ['auth', 'mode'] as const;

export function useAuthMode(): UseQueryResult<AuthModeResponse, Error> {
  return useQuery<AuthModeResponse, Error>({
    queryKey: AUTH_MODE_QUERY_KEY,
    queryFn: getAuthMode,
    // El modo se fija al arrancar el backend; no merece la pena refetch.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

export function useSignup(): UseMutationResult<SignupResponse, Error, SignupRequest> {
  const qc = useQueryClient();
  return useMutation<SignupResponse, Error, SignupRequest>({
    mutationFn: signup,
    onSuccess: async () => {
      // El backend del signup crea la sesión (auto-login) — invalidamos
      // auth status para que el router redirija a `/`.
      await qc.invalidateQueries({ queryKey: ['auth'] });
      await qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

export function useLoginMultiUser(): UseMutationResult<
  { ok: true },
  Error,
  LoginMultiUserRequest
> {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, LoginMultiUserRequest>({
    mutationFn: loginMultiUser,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth'] });
      await qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

export function usePasswordResetRequest(): UseMutationResult<
  { ok: true },
  Error,
  PasswordResetRequestBody
> {
  // No invalidamos nada: la request solo envía un email (o lo loggea) y no
  // cambia estado observable.
  return useMutation<{ ok: true }, Error, PasswordResetRequestBody>({
    mutationFn: requestPasswordReset,
  });
}

export function usePasswordResetConfirm(): UseMutationResult<
  { ok: true },
  Error,
  PasswordResetConfirmBody
> {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, PasswordResetConfirmBody>({
    mutationFn: confirmPasswordReset,
    onSuccess: async () => {
      // Confirmar el reset establece nueva sesión — refrescamos auth.
      await qc.invalidateQueries({ queryKey: ['auth'] });
      await qc.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}
